import { ApolloError } from '@apollo/client'

import { useLazyRecommendedForYou } from '../../graphQL/queries/recommendedForYou'
import {
  IRecommendedForYouResponse,
  IRecommendedForYouVariables,
} from '../../graphQL/queries/recommendedForYou/types'
import {
  setRecommendedForYouItems,
  setRecommendedForYouStatus,
} from '../../redux/slices/recommendations'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import {
  handleRecommendationFetchError,
  mergeCartQuantitiesIntoRecommendationItems,
  skipIfMissingIdentifiers,
} from './recommendationHookHelpers'

const FUNCTION_NAME = 'useRecommendedForYou'

/**
 * Hook that fetches the `recommendedForYou` recommendations carousel data
 * and writes it into the matching Redux slice. Triggered from the
 * EventManager via the `templateActions.recommendedForYou.fetchProducts`
 * template action.
 *
 * Ships dark in Phase 1 — fully wired in code but not yet activated by SCS
 * config for any tenant.
 */
export const useRecommendedForYou = () => {
  const dispatch = useAppDispatch()
  const config = useAppSelector((state) => state.defaultSlice.config)
  const cartItems = useAppSelector((state) => state.cartSlice.items)
  const cartGroups = useAppSelector((state) => state.cartSlice.groups)
  const cartActivePromotions = useAppSelector(
    (state) => state.cartSlice.activePromotions,
  )

  const onCompleted = ({ recommendedForYou }: IRecommendedForYouResponse) => {
    // Apollo invokes onCompleted on a detached microtask whose throws are
    // silently swallowed in production builds. Wrap the merge + dispatch so
    // any throw surfaces as ERROR + Datadog instead of leaving the slice
    // permanently in LOADING.
    try {
      const merged = mergeCartQuantitiesIntoRecommendationItems(
        recommendedForYou.items,
        {
          items: cartItems,
          groups: cartGroups ?? [],
          activePromotions: cartActivePromotions,
        },
        config,
      )
      dispatch(setRecommendedForYouItems(merged))
      dispatch(setRecommendedForYouStatus('OK'))
    } catch (exception) {
      dispatch(setRecommendedForYouStatus('ERROR'))
      handleRecommendationFetchError(
        exception,
        FUNCTION_NAME,
        'recommendedForYou onCompleted threw during cart-quantity merge',
      )
    }
  }

  const onError = (error: ApolloError) => {
    dispatch(setRecommendedForYouStatus('ERROR'))
    handleRecommendationFetchError(
      error,
      FUNCTION_NAME,
      'recommendedForYou fetch failed',
    )
  }

  const [getRecommendedForYou] = useLazyRecommendedForYou({
    onCompleted,
    onError,
  })

  const fetchRecommendedForYou = async (
    variables: IRecommendedForYouVariables,
  ) => {
    if (skipIfMissingIdentifiers(variables, FUNCTION_NAME, 'recommendedForYou')) return
    dispatch(setRecommendedForYouStatus('LOADING'))
    try {
      await getRecommendedForYou({ variables })
    } catch (exception) {
      dispatch(setRecommendedForYouStatus('ERROR'))
      handleRecommendationFetchError(
        exception,
        FUNCTION_NAME,
        'recommendedForYou fetch threw synchronously',
      )
    }
  }

  return fetchRecommendedForYou
}
